import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { getSurfaceTypeOptions } from '@/utilities/collections/newReportOptions'

export const useReportFormStore = defineStore('reportForm', () => {
  // Main form data
  const formData = ref({
    // Step 0: User Information
    user_role: null,
    project_type: null,
    location_type: null,
    // Step 1: Basic project information
    project_name: "",
    project_location: "",
    site_area: null,
    land_use_type: null,
    soil_type: null,
    storm_frequencies: [
      {
        frequency: null,
        duration: null,
        rainfall_intensity: null,
        rainfall_intensity_15min: null,
        rainfall_depth: null,
        loading: false
      }
    ],
    // Step 2: Pre-development surfaces
    pre_development_surfaces: [
      {
        type: null,
        percentage: null,
        coefficient: 0
      }
    ],
    // Step 3: Post-development surfaces and stormwater target
    post_development_surfaces: [
      {
        type: null,
        percentage: null,
        coefficient: 0
      }
    ],
    stormwater_target_m3: null,
    // Mapbox fields
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
    latitude: null,
    longitude: null,
    property_boundary: null,
    // property_area_m2: null,
  })

  // Form validity
  const isFormValid = ref(false)
  const currentStep = ref(0)

  // Computed properties
  const totalSurfacePercentage = computed(() => {
    return formData.value.pre_development_surfaces.reduce((total, surface) => {
      return total + (parseFloat(surface.percentage) || 0)
    }, 0)
  })

  const totalPostDevelopmentSurfacePercentage = computed(() => {
    return formData.value.post_development_surfaces.reduce((total, surface) => {
      return total + (parseFloat(surface.percentage) || 0)
    }, 0)
  })

  const averageRunoffCoefficient = computed(() => {
    if (formData.value.pre_development_surfaces.length === 0) return 0

    const weightedSum = formData.value.pre_development_surfaces.reduce((sum, surface) => {
      const percentage = parseFloat(surface.percentage) || 0
      return sum + (surface.coefficient * percentage)
    }, 0)

    return weightedSum / 100
  })

  const averagePostDevelopmentRunoffCoefficient = computed(() => {
    if (formData.value.post_development_surfaces.length === 0) return 0

    const weightedSum = formData.value.post_development_surfaces.reduce((sum, surface) => {
      const percentage = parseFloat(surface.percentage) || 0
      return sum + (surface.coefficient * percentage)
    }, 0)

    return weightedSum / 100
  })

  // Surface manipulation methods
  const addSurface = () => {
    formData.value.pre_development_surfaces.push({
      type: null,
      percentage: null,
      coefficient: 0
    })
  }

  const removeSurface = (index) => {
    if (formData.value.pre_development_surfaces.length > 1) {
      formData.value.pre_development_surfaces.splice(index, 1)
    }
  }

  const updateSurfaceCoefficient = (index) => {
    const surface = formData.value.pre_development_surfaces[index]
    if (!surface.type) return

    // Get surface options for current project type
    const surfaceOptions = getSurfaceTypeOptions(formData.value.project_type)
    const selectedOption = surfaceOptions.find(option => option.value === surface.type)
    surface.coefficient = selectedOption ? selectedOption.coefficient : 0
  }

  // Post-development surface manipulation methods
  const addPostDevelopmentSurface = () => {
    formData.value.post_development_surfaces.push({
      type: null,
      percentage: null,
      coefficient: 0
    })
  }

  const removePostDevelopmentSurface = (index) => {
    if (formData.value.post_development_surfaces.length > 1) {
      formData.value.post_development_surfaces.splice(index, 1)
    }
  }

  const updatePostDevelopmentSurfaceCoefficient = (index) => {
    const surface = formData.value.post_development_surfaces[index]
    if (!surface.type) return

    // Get surface options for current project type
    const surfaceOptions = getSurfaceTypeOptions(formData.value.project_type)
    const selectedOption = surfaceOptions.find(option => option.value === surface.type)
    surface.coefficient = selectedOption ? selectedOption.coefficient : 0
  }

  // Update methods
  const updateFormValidity = (valid) => {
    isFormValid.value = valid
  }

  const updateMapboxData = (data) => {
    // Update the form data with mapbox values
    formData.value.street_address = data.street_address
    formData.value.city = data.city
    formData.value.state = data.state
    formData.value.zip_code = data.zip_code
    formData.value.latitude = data.latitude
    formData.value.longitude = data.longitude
    formData.value.property_boundary = data.property_boundary
    formData.value.project_location = data.project_location
    // formData.value.property_area_m2 = data.property_area_m2

    // If property area is available from the map, use it to populate the site area field
    if (data.property_area_m2) {
      formData.value.site_area = Math.round(data.property_area_m2)
    }
  }

  // Navigation methods
  const nextStep = () => {
    currentStep.value++
  }

  const prevStep = () => {
    currentStep.value--
  }

  const setStep = (step) => {
    currentStep.value = step
  }

  // Form reset and autofill
  const resetForm = () => {
    formData.value = {
      // Step 0: User Information
      user_role: null,
      project_type: null,
      location_type: null,
      // Step 1: Basic project information
      project_name: "",
      project_location: "",
      site_area: null,
      land_use_type: null,
      soil_type: null,
      storm_frequencies: [
        {
          frequency: null,
          duration: null,
          rainfall_intensity: null,
          rainfall_intensity_15min: null,
          rainfall_depth: null,
          loading: false
        }
      ],
      // Step 2: Pre-development surfaces
      pre_development_surfaces: [
        {
          type: null,
          percentage: null,
          coefficient: 0
        }
      ],
      // Step 3: Post-development surfaces and stormwater target
      post_development_surfaces: [
        {
          type: null,
          percentage: null,
          coefficient: 0
        }
      ],
      stormwater_target_m3: null,
      street_address: "",
      city: "",
      state: "",
      zip_code: "",
      latitude: null,
      longitude: null,
      property_boundary: null,
      // property_area_m2: null,
    }
    currentStep.value = 0
  }

  const autofillForm = (data) => {
    // First, preserve the existing form data structure
    const existingData = { ...formData.value };

    // Update with new data
    Object.assign(existingData, data);

    // Handle storm frequencies
    if (data.storm_frequencies && Array.isArray(data.storm_frequencies)) {
      // If storm frequencies are provided, use them
      existingData.storm_frequencies = data.storm_frequencies.map(storm => ({
        frequency: storm.frequency || null,
        duration: storm.duration || null,
        rainfall_intensity: storm.rainfall_intensity || null,
        rainfall_intensity_15min: storm.rainfall_intensity_15min || null,
        rainfall_depth: storm.rainfall_depth || null,
        loading: false
      }));
    } else {
      // If no storm frequencies provided, ensure at least one empty storm frequency exists
      existingData.storm_frequencies = [{
        frequency: null,
        duration: null,
        rainfall_intensity: null,
        rainfall_intensity_15min: null,
        rainfall_depth: null,
        loading: false
      }];
    }

    // Handle pre-development surfaces
    if (data.surfaces && Array.isArray(data.surfaces)) {
      // If surfaces are provided, use them (backward compatibility)
      existingData.pre_development_surfaces = data.surfaces.map(surface => ({
        type: surface.type || null,
        percentage: surface.percentage || null,
        coefficient: surface.coefficient || 0
      }));
    } else if (data.pre_development_surfaces && Array.isArray(data.pre_development_surfaces)) {
      // If pre_development_surfaces are provided, use them
      existingData.pre_development_surfaces = data.pre_development_surfaces.map(surface => ({
        type: surface.type || null,
        percentage: surface.percentage || null,
        coefficient: surface.coefficient || 0
      }));
    } else {
      // If no surfaces provided, ensure at least one empty surface exists
      existingData.pre_development_surfaces = [{
        type: null,
        percentage: null,
        coefficient: 0
      }];
    }

    // Handle post-development surfaces
    if (data.post_development_surfaces && Array.isArray(data.post_development_surfaces)) {
      existingData.post_development_surfaces = data.post_development_surfaces.map(surface => ({
        type: surface.type || null,
        percentage: surface.percentage || null,
        coefficient: surface.coefficient || 0
      }));
    } else {
      // If no post-development surfaces provided, ensure at least one empty surface exists
      existingData.post_development_surfaces = [{
        type: null,
        percentage: null,
        coefficient: 0
      }];
    }

    // Update the form data
    formData.value = existingData;
  }

  // API data preparation
  const getApiFormData = () => {
    // Create a clean copy without the problematic fields
    const { pre_development_surfaces, post_development_surfaces, property_area_m2, ...cleanData } = formData.value

    return {
      ...cleanData,
      // Transform pre-development surfaces to pre_development_surfaces_attributes expected by Rails
      pre_development_surfaces_attributes: formData.value.pre_development_surfaces.map((surface) => ({
        surface_type: surface.type,
        percentage: surface.percentage,
        coefficient: surface.coefficient,
        development_type: 'pre_development'
      })),
      // Transform post-development surfaces
      post_development_surfaces_attributes: formData.value.post_development_surfaces.map((surface) => ({
        surface_type: surface.type,
        percentage: surface.percentage,
        coefficient: surface.coefficient,
        development_type: 'post_development'
      })),
    }
  }

  // Add a new storm frequency
  const addStormFrequency = () => {
    formData.value.storm_frequencies.push({
      frequency: null,
      duration: null,
      rainfall_intensity: null,
      rainfall_intensity_15min: null,
      rainfall_depth: null,
      loading: false
    })
  }

  // Remove a storm frequency
  const removeStormFrequency = (index) => {
    formData.value.storm_frequencies.splice(index, 1)
  }

  return {
    formData,
    isFormValid,
    currentStep,
    totalSurfacePercentage,
    totalPostDevelopmentSurfacePercentage,
    averageRunoffCoefficient,
    averagePostDevelopmentRunoffCoefficient,
    addSurface,
    removeSurface,
    updateSurfaceCoefficient,
    addPostDevelopmentSurface,
    removePostDevelopmentSurface,
    updatePostDevelopmentSurfaceCoefficient,
    updateFormValidity,
    updateMapboxData,
    nextStep,
    prevStep,
    setStep,
    resetForm,
    autofillForm,
    getApiFormData,
    addStormFrequency,
    removeStormFrequency
  }
})
